import {
  coerceSupportTicketSorts,
  decodeSupportTicketSorts,
  DEFAULT_SUPPORT_TICKET_SORTS,
  encodeSupportTicketSorts,
  isDefaultSupportTicketSorts,
  supportTicketSortsAreEqual,
  type SupportTicketSort,
} from "@redotech/effect-api-shared/schema/query-options";
import {
  decodeSupportTicketFilterGroup,
  encodeSupportTicketFilterGroup,
  supportTicketFilterData,
  SupportTicketFilterNames,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import type { ArrayStringFilterData } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import { FilterOperator } from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import { filterGroupsAreEqual } from "@redotech/redo-model/views/utils/util";
import type { FilterGroup } from "@redotech/redo-model/views/views";
import { SPAM_TAG_EXCLUSION_FILTER } from "./default-views";

export const URL_FILTER_GROUP_PARAM = "fg";
export const URL_SORTS_PARAM = "sorts";

export interface SupportTicketQueryOptions {
  filterGroup: FilterGroup;
  sorts: readonly SupportTicketSort[];
}

const DEFAULT_STATUS_FILTER: ArrayStringFilterData = {
  ...(supportTicketFilterData[
    SupportTicketFilterNames.STATUS
  ] as ArrayStringFilterData),
  value: ["OPEN"],
};

export const DEFAULT_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [DEFAULT_STATUS_FILTER, SPAM_TAG_EXCLUSION_FILTER],
};

export const DEFAULT_QUERY_OPTIONS: SupportTicketQueryOptions = {
  filterGroup: DEFAULT_FILTER_GROUP,
  sorts: DEFAULT_SUPPORT_TICKET_SORTS,
};

export function parseSupportTicketQueryOptionsFromSearch(
  search: string,
): SupportTicketQueryOptions {
  const queryParams = new URLSearchParams(search);
  const fgParam = queryParams.get(URL_FILTER_GROUP_PARAM);
  const sortsParam = queryParams.get(URL_SORTS_PARAM);
  const sorts = decodeSupportTicketSorts(
    sortsParam,
    DEFAULT_QUERY_OPTIONS.sorts,
  );

  if (!fgParam && !sortsParam) {
    return DEFAULT_QUERY_OPTIONS;
  }

  if (!fgParam) {
    return { ...DEFAULT_QUERY_OPTIONS, sorts };
  }

  try {
    const filterGroup = decodeSupportTicketFilterGroup(fgParam);

    if (!filterGroup) {
      return { ...DEFAULT_QUERY_OPTIONS, sorts };
    }

    return { filterGroup, sorts };
  } catch {
    return { ...DEFAULT_QUERY_OPTIONS, sorts };
  }
}

export function supportTicketQueryOptionsAreEqual(
  a: SupportTicketQueryOptions,
  b: SupportTicketQueryOptions,
): boolean {
  return (
    filterGroupsAreEqual(a.filterGroup, b.filterGroup) &&
    supportTicketSortsAreEqual(a.sorts, b.sorts)
  );
}

export function buildSearchWithSupportTicketQueryOptions(
  currentSearch: string,
  queryOptions: SupportTicketQueryOptions,
): string {
  const searchParams = new URLSearchParams(currentSearch);

  if (
    filterGroupsAreEqual(
      queryOptions.filterGroup,
      DEFAULT_QUERY_OPTIONS.filterGroup,
    )
  ) {
    searchParams.delete(URL_FILTER_GROUP_PARAM);
  } else {
    searchParams.set(
      URL_FILTER_GROUP_PARAM,
      encodeSupportTicketFilterGroup(queryOptions.filterGroup),
    );
  }

  if (isDefaultSupportTicketSorts(queryOptions.sorts)) {
    searchParams.delete(URL_SORTS_PARAM);
  } else {
    searchParams.set(
      URL_SORTS_PARAM,
      encodeSupportTicketSorts(queryOptions.sorts),
    );
  }

  searchParams.delete("defaultView");

  const newSearch = searchParams.toString();
  return newSearch ? `?${newSearch}` : "";
}

export function encodeSupportTicketQueryOptionsForRequest(
  queryOptions: SupportTicketQueryOptions,
): string {
  return encodeSupportTicketFilterGroup(queryOptions.filterGroup);
}

/**
 * Whether the active sort differs from what a view would reload. `storedViewSort`
 * is a saved view's raw `sort` value (or `null` with no active view); a missing
 * or invalid stored sort is treated as the default sort. Lets a sort-only change
 * surface the same "Update view" / "Save as new view" affordance that filter and
 * column edits do, and keeps the equality semantics in one tested place.
 */
export function supportTicketViewSortIsDirty(
  currentSorts: readonly SupportTicketSort[],
  storedViewSort: unknown,
): boolean {
  const storedSorts =
    coerceSupportTicketSorts(storedViewSort) ?? DEFAULT_SUPPORT_TICKET_SORTS;
  return !supportTicketSortsAreEqual(currentSorts, storedSorts);
}
